import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api'; 
import '../style/PageStyle/OrganisationManagement.css';

const UserManagementWidget = ({ user, orgId, onClose, onSave }) => {
  const isEditMode = !!user;

  // State for form data
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    job_title: '',
    phone: '',
    role_key: '', 
    status: 'Active',
    password: ''
  });

  // State for dynamic roles
  const [availableRoles, setAvailableRoles] = useState([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);

  // 1. Fetch Roles (Groups) on Mount
  useEffect(() => {
    const fetchRoles = async () => {
      setIsLoadingRoles(true);
      try {
        const groups = await apiService.getGroups();
        setAvailableRoles(groups);
        
        // If adding a new user, set default role to the first one found
        if (!isEditMode && groups.length > 0 && !formData.role_key) {
          // API returns 'roleKey', so we use that
          setFormData(prev => ({ ...prev, role_key: groups[0].roleKey }));
        }
      } catch (error) {
        console.error("Failed to fetch user roles", error);
      } finally {
        setIsLoadingRoles(false);
      }
    };

    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2. Initialize Form Data from User Prop
  useEffect(() => {
    if (user) {
      setFormData({
        username: user.name || user.username || '',
        email: user.email || '',
        job_title: user.job_title || '', 
        phone: user.phone || '',
        role_key: user.role || user.roleKey || '', 
        status: user.status || 'Active',
        password: '' 
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.username || !formData.email) {
      alert("Name and Email are required.");
      return;
    }
    
    // --- CHANGE: Skip password check if contact_only ---
    if (!isEditMode && formData.role_key !== 'contact_only' && !formData.password) {
      alert("Password is required for new users.");
      return;
    }
    
    if (!formData.role_key) {
      alert("Please select a user role.");
      return;
    }

    // --- CONSTRUCT PAYLOAD ---
    const payload = {
      username: formData.username,
      email: formData.email,
      jobTitle: formData.job_title, 
      phone: formData.phone,
      status: formData.status,
      password: formData.password,
      organisationId: orgId, 
      role: formData.role_key 
    };

    if (isEditMode) {
      payload.id = user.id;
    }

    onSave(payload);
  };

  // --- NEW HELPER ---
  const isContactOnly = formData.role_key === 'contact_only';

  return (
    <div className="widget-overlay" style={{ zIndex: 2000, backgroundColor: 'rgba(0,0,0,0.6)' }}> 
      <div className="widget-container" style={{ maxWidth: '450px', width: '100%', marginTop: '50px' }}>
        
        <div className="widget-header">
          <h3>{isEditMode ? 'Edit User' : 'Add New User'}</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="widget-content">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Name *</label>
              <input 
                type="text" name="username" className="widget-input" 
                value={formData.username} onChange={handleChange} autoFocus
              />
            </div>

            <div className="form-group">
              <label>Email *</label>
              <input 
                type="email" name="email" className="widget-input" 
                value={formData.email} onChange={handleChange} 
              />
            </div>

            <div className="form-group">
              <label>Job Title</label>
              <input 
                type="text" name="job_title" className="widget-input" 
                value={formData.job_title} onChange={handleChange} 
              />
            </div>
            
            <div className="form-group">
              <label>Phone</label>
              <input 
                type="text" name="phone" className="widget-input" 
                value={formData.phone} onChange={handleChange} 
              />
            </div>

            <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <div className="form-group">
                <label>Role *</label>
                <select 
                  name="role_key" 
                  className="widget-input" 
                  value={formData.role_key} 
                  onChange={handleChange}
                  disabled={isLoadingRoles}
                >
                  {isLoadingRoles ? (
                    <option>Loading roles...</option>
                  ) : (
                    <>
                      <option value="">Select Role</option>
                      {availableRoles.map((group) => (
                        <option 
                          key={group.id} 
                          value={group.roleKey}
                        >
                          {group.name}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </div>
              <div className="form-group">
                <label>Status</label>
                <select name="status" className="widget-input" value={formData.status} onChange={handleChange}>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                  <option value="Pending">Pending</option>
                </select>
              </div>
            </div>

            {/* --- CHANGE: Hide Password Field if Contact Only --- */}
            {!isContactOnly && (
              <div className="form-group">
                <label>{isEditMode ? 'New Password (Optional)' : 'Password *'}</label>
                <input 
                  type="password" name="password" className="widget-input" 
                  value={formData.password} onChange={handleChange} 
                  placeholder={isEditMode ? "Leave blank to keep current" : ""}
                />
              </div>
            )}

            <div className="tab-actions" style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button type="button" className="action-btn" onClick={onClose} style={{ marginRight: '10px', background: '#eee', color: '#333' }}>Cancel</button>
              <button type="submit" className="action-btn primary">{isEditMode ? 'Update User' : 'Create User'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserManagementWidget;
